import { useId, useRef, useState } from 'react';
import { jobRoleGateway } from '../index';
import { isForbidden, jobRoleErrorMessage } from '../jobRoleErrors';
import type { JobRoleDetail } from '../types';
import { ModalDialog } from './ModalDialog';

export interface DuplicateRoleDialogProps {
  role: { id: string; title: string };
  onClose: () => void;
  onDuplicated: (created: JobRoleDetail) => void;
  onForbidden?: () => void;
}

/** Duplicate: a new Draft copy of a role under a new title (and optionally a new internal code). The original is never changed. */
export function DuplicateRoleDialog({ role, onClose, onDuplicated, onForbidden }: DuplicateRoleDialogProps) {
  const uid = useId();
  const [title, setTitle] = useState(`${role.title} (copy)`);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  async function submit() {
    const clean = title.trim();
    if (!clean) {
      setTitleError('Enter a title for the copy.');
      titleRef.current?.focus();
      return;
    }
    setTitleError(null);
    setError(null);
    setBusy(true);
    try {
      const created = await jobRoleGateway.duplicateRole(role.id, { title: clean, roleCode: code.trim() || null });
      onDuplicated(created);
    } catch (err) {
      if (isForbidden(err)) onForbidden?.();
      setError(jobRoleErrorMessage(err, 'Could not duplicate this role.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title={`Duplicate ${role.title}`}
      size="sm"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => void submit()} disabled={busy}>{busy ? 'Duplicating…' : 'Create draft copy'}</button>
        </>
      )}
    >
      <p className="muted">The copy starts as a Draft with the same description. The original role is not changed.</p>
      <div className="jr-field">
        <label htmlFor={`${uid}-title`}>New role title <span aria-hidden="true" className="jr-req">*</span></label>
        <input
          ref={titleRef} id={`${uid}-title`} data-autofocus type="text" value={title} maxLength={150} autoComplete="off"
          aria-required="true" aria-invalid={titleError ? true : undefined} aria-describedby={titleError ? `${uid}-title-error` : undefined}
          onChange={(e) => { setTitle(e.target.value); setTitleError(null); }}
        />
        {titleError && <p id={`${uid}-title-error`} className="jr-error" role="alert">{titleError}</p>}
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-code`}>Internal code (optional)</label>
        <input id={`${uid}-code`} type="text" value={code} maxLength={40} autoComplete="off" onChange={(e) => setCode(e.target.value)} />
      </div>
      {error && <p className="jr-error" role="alert">{error}</p>}
    </ModalDialog>
  );
}
